<template>
  <div class="block" id="customer">
    <nav class="breadcrumb" v-if="showBreadCrumbs">
      <ul>
        <li>
          <router-link class="p-0 m-0" :to="{ path: '/customers' }"><a>Customers</a></router-link>
        </li>
        <li class="is-active">
          <span class="p-0 ml-2">{{ customer.name || 'new' }}</span>
        </li>
      </ul>
    </nav>
    <div class="columns is-vcentered">
      <div class="column">
        <div class="header">{{ customerId === 'new' ? 'Create' : 'Edit' }} Customer</div>
      </div>
      <div class="column is-narrow">
        <b-button v-if="customerToEdit.id && userCanMerge" @click="isMergeActive = true">Merge Customer</b-button>
      </div>
    </div>
    <select-customer-modal
      :active="isMergeActive"
      :disabled-item="disabledCustomerCheck"
      @on-close="isMergeActive = false"
      @on-select="mergeSelected"
    >
      <template #before-content>
        <b-message class="is-size-7" type="is-info">
          <strong>Note:</strong>
          <span class="ml-1">
            This action will merge two customers contacts, job sites and service work orders. This action is not
            reversable, but you will have an opportunity to review the merge before the operation is complete.
          </span>
        </b-message>
      </template>
    </select-customer-modal>
    <b-modal
      v-model="isAddContactActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
      :full-screen="isFullScreen"
    >
      <template #default>
        <form class="modal-card" @submit.prevent="addContact">
          <header class="modal-card-head">{{ contactIndexIsSet ? `New Contact` : `Edit Contact` }}</header>
          <section class="modal-card-body">
            <div class="columns">
              <div class="column">
                <b-field label="Name">
                  <b-input id="contact-name" v-model="contactToCreate.name"></b-input>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Email *" :type="emailFieldType" :message="emailErrorMessage">
                  <b-input id="contact-email" v-model="contactToCreate.email" type="email" required></b-input>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Phone Number">
                  <b-input id="contact-phone" v-model="contactToCreate.phone"></b-input>
                </b-field>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <b-field>
                  <b-switch id="contact-primary" v-model="contactToCreate.canTextCommunication">
                    Text Communications
                  </b-switch>
                </b-field>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button
              id="add"
              native-type="submit"
              size="is-small"
              label="Submit"
              type="is-primary"
              :disabled="!contactNameIsSet || !isContactEmailValid"
            ></b-button>
            <b-button id="cancel" size="is-small" label="Cancel" type="is-danger" @click="cancelContact"></b-button>
          </footer>
        </form>
      </template>
    </b-modal>
    <div v-if="!customerIsReadyForEdit">
      <div>Loading</div>
      <b-progress></b-progress>
    </div>
    <form class="block" v-else @submit.prevent="upsert">
      <b-message class="is-danger" v-if="customerToEdit.deleted">
        Customer has been merged into a new customer record
      </b-message>
      <div class="columns" v-if="!isNew">
        <div class="column">
          <b-field>
            <b-switch id="customer-deleted" v-model="customerToEdit.deleted" :disabled="!userCanDelete">
              Deleted
            </b-switch>
          </b-field>
        </div>
      </div>
      <div class="columns" v-if="isProspectActive">
        <div class="column">
          <b-field>
            <b-switch id="customer-id" v-model="customerToEdit.isProspective">Prospective</b-switch>
          </b-field>
        </div>
        <div class="column" v-if="customerToEdit.isProspective">
          <div>Prospective Types</div>
          <b-field>
            <b-dropdown v-model="customerToEdit.prospectiveTypes" multiple>
              <template #trigger>
                <b-button icon-right="caret-down">
                  Selected ({{ customerToEdit.prospectiveTypes?.length || 0 }})
                </b-button>
              </template>

              <b-dropdown-item v-for="type in customerProspectiveTypes" :value="type" :key="type">
                <span>{{ type }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>
      </div>
      <div class="flex flex-row my-3" v-if="!customerToEdit.isProspective && customerToEdit.prospectiveTypes?.length">
        <div class="text-md font-bold mr-2">Old Prospective Classification</div>
        <b-field
          class="flex flex-row items-center"
          :key="index"
          v-for="(type, index) in customerToEdit.prospectiveTypes"
        >
          <b-tag class="mr-1">{{ type }}</b-tag>
        </b-field>
      </div>
      <div class="columns">
        <div class="column">
          <b-field label="Name">
            <b-input
              id="customer-name"
              v-model="customerToEdit.name"
              required
              :disabled="customerToEdit.deleted"
            ></b-input>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Display As Name">
            <b-input
              id="customer-display-as-name"
              v-model="customerToEdit.displayAsName"
              required
              :disabled="customerToEdit.deleted"
            ></b-input>
          </b-field>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <b-field label="Account Status" expanded>
            <b-select
              id="customer-account-status"
              v-model="customerToEdit.accountingStatus"
              expanded
              :disabled="customerToEdit.deleted"
            >
              <option v-for="status in accountingStatus" :value="status.value" :key="status.value">
                {{ status.label }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Discount Status">
            <b-select
              id="customer-discount-status"
              v-model="customerToEdit.discountStatus"
              expanded
              :disabled="customerToEdit.deleted"
            >
              <option v-for="status in discountStatus" :value="status.value" :key="status.value">
                {{ status.label }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <b-field label="Customer Account Notes">
            <b-input
              id="customer-account-notes"
              type="textarea"
              v-model="customerToEdit.accountNotes"
              :disabled="customerToEdit.deleted"
            ></b-input>
          </b-field>
        </div>
      </div>
      <div class="p-0 m-0 columns">
        <div class="p-0 m-0 column">
          <div class="my-4 is-size-3">Contacts</div>
        </div>
        <div class="pt-5 m-0 column">
          <b-tooltip class="is-pulled-right" label="Add a new contact">
            <b-button
              class="is-small is-info"
              icon-left="plus"
              @click="isAddContactActive = true"
              :disabled="customerToEdit.deleted"
            ></b-button>
          </b-tooltip>
        </div>
      </div>
      <div class="container" v-if="!customerToEdit.contacts.length">
        <b-message class="m-4">No Contacts</b-message>
      </div>
      <div class="line-items" v-else>
        <div
          class="p-3 m-0 columns is-gapless is-vcentered"
          v-for="(c, index) in customerToEdit.contacts"
          :key="index"
          :class="{
            'is-even': index % 2 === 0,
            'is-odd': index % 2 !== 0,
            'has-background-warning': (customerToMerge.contacts || []).includes(c),
          }"
        >
          <div class="line-item-details column">
            <div class="line-item-info">
              <div>
                <span class="field-name">Name</span>
                <span>{{ c.name }}</span>
              </div>
              <div>
                <span class="field-name">Email</span>
                <span>{{ c.email }}</span>
              </div>
              <div>
                <span class="field-name">Phone Number</span>
                <span>{{ c.phone }}</span>
              </div>
            </div>
            <div class="line-item-info">
              <div class="buttons">
                <b-button
                  class="is-small is-primary"
                  icon-left="edit"
                  @click="editContact(c, index)"
                  :disabled="customerToEdit.deleted"
                ></b-button>
                <b-button
                  class="is-small is-danger"
                  icon-left="trash"
                  @click="removeContact(c, index)"
                  :disabled="customerToEdit.deleted"
                ></b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="is-size-3 contact-header">Billing</div>
      <flowright-address v-model="customerToEdit.billing" :deleted="customerToEdit.deleted"></flowright-address>
      <div class="columns">
        <div class="column">
          <comments-view
            :association-id="customerId"
            :displayable-types="displayableTypes"
            :comment-type="commentType"
            :disabled="isNew"
          ></comments-view>
        </div>
      </div>
      <div v-if="!isNew">
        <div class="p-0 m-0 columns">
          <div class="p-0 m-0 column">
            <div class="my-4 is-size-3">Job Sites</div>
          </div>
          <div class="pt-5 m-0 column">
            <b-tooltip class="is-pulled-right" label="Add a new jobsite">
              <b-button
                class="is-small is-info"
                id="jobsite-add"
                icon-left="plus"
                @click="newJobSite"
                :disabled="customerToEdit.deleted"
              ></b-button>
            </b-tooltip>
          </div>
        </div>
        <div class="container" v-if="!jobSites || jobSites.length === 0">
          <b-message class="m-4">No Job Sites</b-message>
        </div>
        <div class="line-items" v-else>
          <div
            class="p-3 m-0 columns is-gapless is-vcentered"
            v-for="(c, index) in jobSites"
            :key="c.id"
            :class="{
              'is-even': index % 2 === 0,
              'is-odd': index % 2 !== 0,
            }"
          >
            <div class="line-item-details column">
              <div class="line-item-info">
                <div v-if="c.nickname">
                  <span class="field-name">Nickname</span>
                  <span>{{ c.nickname }}</span>
                </div>
                <div>
                  <span class="field-name">Address</span>
                  <span>{{ c.street }} {{ c.city }} {{ c.state }} {{ c.zip }}</span>
                  <b-tag v-if="c.doNotService" type="is-danger" class="ml-2">DNS</b-tag>
                </div>
                <div>
                  <span class="field-name">EzCare</span>
                  <span>{{ getEzCareLabel(c.ezCare) }}</span>
                  <template v-if="!!c.ezCareStartDate">
                    <span class="ml-5 font-bold">Start Date:</span>
                    <span class="ml-2">{{ c.ezCareStartDate | formatDate }}</span>
                  </template>
                </div>
              </div>
              <div class="line-item-info">
                <div class="buttons">
                  <b-button class="is-small is-primary" icon-left="edit" @click="editJobsite(c)"></b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mb-4 work-order-history">
          <div class="header is-size-3">Work Order History</div>
          <b-field label="Job Site">
            <b-select class="mb-3" v-model="jobsiteId" placeholder="Select a name">
              <option v-for="c in jobSites" :key="c.id" :value="c.id">
                {{ c.nickname ? c.nickname + ' — ' : '' }}{{ c.street }} {{ c.city }} {{ c.state }} {{ c.zip }}
              </option>
            </b-select>
          </b-field>
          <workorder-site-history :job-site-id="jobsiteId"></workorder-site-history>
        </div>
        <accounting-ledger-integration :customer-id="customerId"></accounting-ledger-integration>
      </div>
      <div v-else>
        <b-message class="m-4">Create customer before adding Job Sites</b-message>
      </div>
      <div class="buttons is-pulled-right">
        <b-button @click="goBack" :disabled="!customerIsReadyForEdit">Cancel</b-button>
        <b-button native-type="submit" type="is-success" :disabled="!customerIsReadyForEdit">
          {{ customerToMerge.id ? 'Merge' : 'Save' }}
        </b-button>
        <b-tooltip
          v-if="bookingEnabled && !(customerToEdit.deleted || wasDeleted)"
          label="Book a Work Order"
          position="is-left"
        >
          <b-button class="is-primary" icon-left="play-circle" @click="saveAndBook" :disabled="customerToEdit.deleted">
            {{ isNew ? 'Create & Book' : 'Book' }}
          </b-button>
        </b-tooltip>
      </div>
    </form>
  </div>
</template>

<script>
import { isEmpty } from 'lodash';
import { CommentType } from '@newmoon-org/shared';
import { mapGetters } from 'vuex';

import CommentsService from '@/service/comments.service';
import WorkOrderService from '@/service/workorders.service';
import { getByCustomerId } from '@/service/jobSite.service';

import FlowrightAddress from '@/components/address/FlowrightAddress.vue';
import WorkorderSiteHistory from '@/components/workorders/WorkorderSiteHistory.vue';
import SelectCustomerModal from '@/components/customers/SelectCustomerModal.vue';
import AccountingLedgerIntegration from '@/components/customers/AccountingLedgerIntegration.vue';

import CustomerService, { EZ_CARE_TYPES } from '../service/customer.service';

import CommentsView from '@/pages/CommentsView.vue';

export default {
  name: 'CustomerEditPage',
  components: {
    CommentsView,
    FlowrightAddress,
    WorkorderSiteHistory,
    SelectCustomerModal,
    AccountingLedgerIntegration,
  },
  props: {
    bookingEnabled: {
      type: Boolean,
      default: true,
    },

    componentCustomerId: {
      type: String,
      default: null,
    },
    showBreadCrumbs: {
      type: Boolean,
      default: true,
    },
    cancelAction: {
      type: Function,
      default: null,
    },
    postSaveAction: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      ezCareTypes: [...EZ_CARE_TYPES],
      commentType: CommentType.CUSTOMER,
      displayableTypes: [CommentType.CUSTOMER],
      customerToEdit: {},
      isLoading: false,
      isAddContactActive: false,
      contactToCreate: { name: '', email: '', phone: '', canTextCommunication: false },
      contactIndexToEdit: '',
      wasDeleted: false,
      accountingStatus: [...CustomerService.accountStatusOptions],
      discountStatus: [...CustomerService.discountStatusOptions],
      isMergeActive: false,
      customerToMerge: {},
      jobsiteId: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),
    ...mapGetters('features', ['isFeatureEnabled']),
    isProspectActive() {
      return this.isFeatureEnabled('prospectiveCustomers.active');
    },
    loggedInUser() {
      return this.user?.employee ?? {};
    },
    isNew() {
      return this.customerId === 'new';
    },
    customerId() {
      return this.componentCustomerId ?? this.$route.params.customerId;
    },
    customerIsReadyForEdit() {
      return !isEmpty(this.customerToEdit);
    },
    contactNameIsSet() {
      return !isEmpty(this.contactToCreate.name);
    },
    contactIndexIsSet() {
      return this.contactIndexToEdit === '';
    },
    isContactEmailValid() {
      // Email is required and must be valid format
      if (isEmpty(this.contactToCreate.email)) {
        return false;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.contactToCreate.email);
    },
    emailFieldType() {
      if (isEmpty(this.contactToCreate.email)) {
        return '';
      }
      return this.isContactEmailValid ? '' : 'is-danger';
    },
    emailErrorMessage() {
      if (this.isContactEmailValid) {
        return '';
      }
      if (isEmpty(this.contactToCreate.email)) {
        return 'Email is required';
      }
      return 'Please enter a valid email address';
    },
    isFullScreen() {
      return this.$isMobile();
    },
    userCanDelete() {
      return ['hr', 'developer', 'global administrator', 'administrator'].includes(
        this.loggedInUser.workflowFunction?.name?.toLowerCase()
      );
    },
    userCanMerge() {
      return this.hasPermission('customer:write');
    },
  },
  watch: {
    'customerToEdit.name': {
      handler(val) {
        this.customerToEdit.displayAsName = val;
      },
    },
  },

  asyncComputed: {
    customer: {
      async get() {
        if (!this.customerId) {
          return {};
        }
        if (this.customerId === 'new') {
          this.customerToEdit = CustomerService.defaultCustomer();
          return { name: 'new' };
        }

        try {
          this.isLoading = true;
          const customer = await CustomerService.get(this.customerId);
          this.wasDeleted = customer.deleted;
          this.isLoading = false;
          this.customerToEdit = { ...customer };
          return customer;
        } catch (e) {
          this.notify(`There was an error loading the customer`, 'is-error');
          return {};
        }
      },
      default: {},
    },
    itemsToMerge: {
      async get() {
        const { id, contacts } = this.customerToMerge;
        const workorders = await WorkOrderService.getByCustomerId(id);
        return { contacts, workorders };
      },
      default: {},
      shouldUpdate() {
        return this.customerToMerge.id;
      },
    },
    customerProspectiveTypes: {
      async get() {
        return CustomerService.getProspectiveTypes();
      },
      default: [],
    },
    jobSites: {
      async get() {
        // artificial delay to give time for the event sourcing to catch up
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(getByCustomerId(this.customerId));
          }, 800);
        });
      },
      default: [],
    },
  },
  methods: {
    newJobSite() {
      this.$router.push({
        name: 'jobsite-details',
        params: {
          id: 'new',
        },
        query: {
          customerId: this.customerId,
        },
      });
    },
    getEzCareLabel(value = null) {
      const ezCare = this.ezCareTypes.find(it => it?.value?.toLowerCase() === value);
      return ezCare ? ezCare.label : 'None';
    },
    async remove() {
      const { result } = await this.$buefy.dialog.confirm({
        message: 'Are you sure? You want to Delete this customer',
        closeOnConfirm: true,
      });

      if (result) {
        await CustomerService.softDelete(this.customerId);
        await this.goBack();
      }
    },
    cancelContact() {
      this.contactToCreate = { name: '', email: '', phone: '' };
      this.contactIndexToEdit = '';
      this.isAddContactActive = false;
    },
    addContact() {
      if (this.contactIndexToEdit !== '') {
        this.customerToEdit.contacts.splice(this.contactIndexToEdit, 1, this.contactToCreate);
        this.cancelContact();
        return;
      }
      this.customerToEdit.contacts.push(this.contactToCreate);
      this.contactToCreate = { name: '', email: '', phone: '' };
      this.cancelContact();
    },
    editContact(c, idx) {
      this.isAddContactActive = true;
      this.contactToCreate = c;
      this.contactIndexToEdit = idx;
    },
    async removeContact(c, idx) {
      if (!c.name) {
        //shortcut if this contact isnt saved
        this.customerToEdit.contacts.splice(idx, 1);
        return;
      }
      const { result } = await this.$buefy.dialog.confirm({
        message: 'Are you sure?',
        closeOnConfirm: true,
      });

      if (result) {
        this.customerToEdit.contacts.splice(idx, 1);
      }
    },
    cancelJobsite() {
      this.jobsiteToCreate = {
        street: '',
        city: '',
        state: '',
        zip: '',
        address: '',
        id: '',
      };
      this.jobsiteEzCare = {
        ezCare: null,
        ezCareStartDate: null,
      };
      this.jobsiteIndexToEdit = '';
      this.isAddJobsiteActive = false;
    },
    ezCareChanged() {
      if (!this.jobsiteEzCare.ezCare) {
        this.jobsiteEzCare.ezCareStartDate = null;
      }
    },
    editJobsite(c) {
      this.$router.push({
        name: 'jobsite-details',
        params: {
          id: c.id,
        },
      });
    },
    async saveAndBook($event) {
      const { id } = await this.save($event);
      await this.$router.push({
        name: 'customer-booking',
        params: {
          customerId: id,
          workOrderId: 'new',
        },
      });
    },
    async upsert($event) {
      const fn = this.isNew ? this.create : this.save;
      await fn($event);
    },
    async create($event) {
      const { id } = await this.save($event);
      await this.$router.push({
        name: 'customer-edit',
        params: {
          customerId: id,
        },
      });
    },
    async confirmSave(customerToMerge) {
      if (!customerToMerge.id) {
        return true;
      }
      const { contacts, workorders } = this.itemsToMerge;
      const { result } = await this.$buefy.dialog.confirm({
        title: 'Merge Confirmation',
        message: `${contacts.length} contact(s), and ${workorders.length} service work order(s) will be merged into ${this.customerToEdit.name} and ${this.customerToMerge.name} will be archived.`,
        confirmText: 'Merge',
      });

      return result;
    },
    fillComputedValues() {
      this.customerToEdit.billing.address = `${this.customerToEdit.billing.street} ${this.customerToEdit.billing.city} ${this.customerToEdit.billing.state} ${this.customerToEdit.billing.zip}`;
    },
    async save(e) {
      e.preventDefault();

      this.fillComputedValues();

      if (!this.customerToEdit.contacts.length) {
        this.notify('You must have at least 1 contact.', 'is-danger');
        throw new Error('Validation Error');
      }

      // confirm with user if merging
      const shouldSave = await this.confirmSave(this.customerToMerge);
      if (!shouldSave) {
        return;
      }

      // show loading since transaction can take some time
      const loadingComponent = this.$buefy.loading.open();
      const isMerging = this.customerToMerge.id;

      try {
        const { id } = await CustomerService.save(this.customerToEdit, this.customerToMerge);

        // audit record
        if (isMerging) {
          const { contacts, workorders } = this.itemsToMerge;
          await CommentsService.create({
            associationId: this.customerToEdit.id,
            type: CommentType.CUSTOMER,
            value: `Successfully merged ${contacts.length} contact(s), and ${workorders.length} service work order(s) from ${this.customerToMerge.name} into ${this.customerToEdit.name}.`,
          });
        }

        this.customerToMerge = {};

        this.notify(
          isMerging
            ? `Merged ${this.customerToMerge.name} into ${this.customerToEdit.name}.`
            : `Saved Customer ${this.customerToEdit.name}`
        );
        return { id };
      } catch (e) {
        console.error(e);
        this.notify(`There was an error saving customer ${this.customerToEdit.name}, please try again.`, 'is-error');
      } finally {
        loadingComponent.close();
      }
    },
    notify(message, type = 'is-success') {
      this.$buefy.notification.open({
        message,
        type,
      });
    },
    async goBack() {
      if (!this.cancelAction) {
        await this.$router.go(-1);
      } else {
        this.cancelAction();
      }
    },
    disabledCustomerCheck(customer) {
      return customer.objectID === this.customerToEdit.id;
    },
    async mergeSelected(customerToMerge) {
      this.isMergeActive = false;
      this.customerToMerge = await CustomerService.get(customerToMerge.objectID);

      // copy contacts and jobsites
      this.customerToEdit = {
        ...this.customerToEdit,
        contacts: [...this.customerToEdit.contacts, ...this.customerToMerge.contacts],
      };
    },
    highlightWorkOrdersToBeMerged() {
      // we dont need to calculate if we should add this class because
      // of how we render one history component for merging WO's and one for the existing WO's.
      return ['has-background-warning'];
    },
  },
};
</script>

<style scoped lang="scss">
#customer {
  max-width: 1600px;
}
.line-item-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.line-items {
  margin-bottom: 15px;
  border: #ddd solid 1px;
  border-radius: 5px;

  .is-odd {
    background-color: #eff1fa;
  }

  .line-item-details {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .line-item-actions {
      flex-direction: row;
      display: flex;
    }
  }
}

.field-name {
  font-weight: bold;
  margin-right: 1em;
}

.contact-header {
  margin-bottom: 0.5em;
}

.the-contact-header {
  display: flex;
  flex-direction: row;
  margin-bottom: 0.5em;
  align-items: center;

  .add-contact {
    margin-left: 1em;
  }
}
</style>
